import { z } from 'zod';

// src/access.ts
function updateSchema(schema) {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [
      key,
      unwrapDefault(field).optional()
    ])
  );
  return z.object(shape);
}
function unwrapDefault(field) {
  let current = field;
  while (current instanceof z.ZodDefault) current = current.def.innerType;
  return current;
}

// src/validators/cnpj.ts
function normalizeCnpj(input) {
  return input.replace(/\D/g, "");
}
function isValidCnpj(input) {
  const cnpj = normalizeCnpj(input);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;
  const digit = (length) => {
    const weights = length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(cnpj[i]) * (weights[i] ?? 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return digit(12) === Number(cnpj[12]) && digit(13) === Number(cnpj[13]);
}
function formatCnpj(input) {
  const c = normalizeCnpj(input);
  if (c.length !== 14) return input;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

// src/validators/cpf.ts
function normalizeCpf(input) {
  return input.replace(/\D/g, "");
}
function isValidCpf(input) {
  const cpf = normalizeCpf(input);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  return checkDigit(cpf, 9) === Number(cpf[9]) && checkDigit(cpf, 10) === Number(cpf[10]);
}
function checkDigit(cpf, length) {
  let sum = 0;
  for (let i = 0; i < length; i++) {
    sum += Number(cpf[i]) * (length + 1 - i);
  }
  const rest = sum * 10 % 11;
  return rest === 10 ? 0 : rest;
}
function formatCpf(input) {
  const cpf = normalizeCpf(input);
  if (cpf.length !== 11) return input;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}
function maskCpf(input) {
  const cpf = normalizeCpf(input);
  if (cpf.length !== 11) return "***.***.***-**";
  return `***.***.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

// src/resident-features.ts
var RESIDENT_FEATURES = [
  "doors",
  "cameras",
  "calls",
  "announcements",
  "visitors",
  "packages",
  "finance",
  "history",
  "face"
];
var RESIDENT_FEATURE_LABELS = {
  doors: "Abrir portas",
  cameras: "Ver c\xE2meras",
  calls: "Receber chamadas do port\xE3o",
  announcements: "Avisos do condom\xEDnio",
  visitors: "Convidar visitantes",
  packages: "Encomendas",
  finance: "Financeiro da unidade",
  history: "Hist\xF3rico de acessos",
  face: "Biometria pelo app"
};
var BASIC = {
  doors: true,
  cameras: true,
  calls: true,
  announcements: true,
  visitors: false,
  packages: false,
  finance: false,
  history: false,
  face: true
};
var DEFAULT_RESIDENT_FEATURES = {
  proprietario: {
    doors: true,
    cameras: true,
    calls: true,
    announcements: true,
    visitors: true,
    packages: true,
    finance: true,
    history: true,
    face: true
  },
  inquilino: { ...BASIC },
  dependente: { ...BASIC },
  funcionario: { ...BASIC, cameras: false, announcements: false },
  autorizado: { ...BASIC, cameras: false, announcements: false }
};
function permissiveResidentFeatures() {
  const all = Object.fromEntries(RESIDENT_FEATURES.map((f) => [f, true]));
  return {
    proprietario: { ...all },
    inquilino: { ...all },
    dependente: { ...all },
    funcionario: { ...all },
    autorizado: { ...all }
  };
}
function residentFeatureFor(matrix, role, feature) {
  return matrix?.[role]?.[feature] ?? DEFAULT_RESIDENT_FEATURES[role][feature];
}
function residentFeaturesFor(matrix, roles, modules) {
  const list = [...roles].filter((r) => r in DEFAULT_RESIDENT_FEATURES);
  const effective = list.length ? list : ["funcionario"];
  const out = Object.fromEntries(RESIDENT_FEATURES.map((f) => [f, false]));
  for (const role of effective)
    for (const f of RESIDENT_FEATURES) if (residentFeatureFor(matrix, role, f)) out[f] = true;
  if (modules) {
    out.visitors &&= modules.visitors;
    out.packages &&= modules.packages;
    out.announcements &&= modules.announcements;
    out.finance &&= modules.finance;
  }
  return out;
}
var phoneE164Schema = z.string().regex(/^\+[1-9]\d{7,14}$/, "Informe um celular v\xE1lido com DDD");
function normalizePhoneBR(input) {
  const digits = input.replace(/\D/g, "");
  if (!digits) return null;
  let national;
  if (input.trim().startsWith("+") && !digits.startsWith("55")) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    national = digits.slice(2);
  } else if (digits.length === 10 || digits.length === 11) {
    national = digits;
  } else {
    return null;
  }
  const ddd = Number(national.slice(0, 2));
  if (ddd < 11 || ddd > 99) return null;
  const subscriber = national.slice(2);
  if (subscriber.length === 9 && !subscriber.startsWith("9")) return null;
  if (subscriber.length === 8 && !/^[2-5]/.test(subscriber)) return null;
  return `+55${national}`;
}
function formatPhoneBR(e164) {
  const match = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  if (!match) return e164;
  return `(${match[1]}) ${match[2]}-${match[3]}`;
}
function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// src/validators/plate.ts
function normalizePlate(input) {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function plateFormatOf(input) {
  const plate = normalizePlate(input);
  if (/^[A-Z]{3}\d{4}$/.test(plate)) return "antiga";
  if (/^[A-Z]{3}\d[A-Z]\d{2}$/.test(plate)) return "mercosul";
  return null;
}
function isValidPlate(input) {
  return plateFormatOf(input) !== null;
}
function formatPlate(input) {
  const plate = normalizePlate(input);
  return plateFormatOf(plate) === "antiga" ? `${plate.slice(0, 3)}-${plate.slice(3)}` : plate;
}

// src/validators/timezone.ts
function isValidTimezone(value) {
  try {
    new Intl.DateTimeFormat("pt-BR", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
var BRAZIL_TIMEZONES = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Cuiaba",
  "America/Campo_Grande",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Bahia",
  "America/Porto_Velho",
  "America/Boa_Vista",
  "America/Rio_Branco",
  "America/Noronha"
];

// src/registry.ts
var ERASED_PERSON_NAME = "Pessoa removida";
var UNIT_PERSON_ROLES = [
  "proprietario",
  "inquilino",
  "dependente",
  "funcionario",
  "autorizado"
];
var UNIT_PERSON_ROLE_LABELS = {
  proprietario: "Propriet\xE1rio",
  inquilino: "Inquilino",
  dependente: "Dependente",
  funcionario: "Funcion\xE1rio",
  autorizado: "Autorizado"
};
var AUTHORIZATION_KINDS = ["diarista", "cuidador", "baba", "prestador", "outro"];
var AUTHORIZATION_KIND_LABELS = {
  diarista: "Diarista",
  cuidador: "Cuidador(a)",
  baba: "Bab\xE1",
  prestador: "Prestador de servi\xE7o",
  outro: "Outro"
};
var ACCESS_POINT_TYPES = ["portao_social", "portao_garagem", "porta", "catraca"];
var ACCESS_POINT_TYPE_LABELS = {
  portao_social: "Port\xE3o social",
  portao_garagem: "Port\xE3o da garagem",
  porta: "Porta",
  catraca: "Catraca"
};
var ACCESS_DIRECTIONS = ["entrada", "saida", "ambos"];
var ACCESS_DIRECTION_LABELS = {
  entrada: "Entrada",
  saida: "Sa\xEDda",
  ambos: "Entrada e sa\xEDda"
};
var CAMERA_SOURCES = ["rtsp", "nvr_channel", "onvif"];
var CAMERA_SOURCE_LABELS = {
  rtsp: "RTSP direto",
  nvr_channel: "Canal do NVR",
  onvif: "ONVIF"
};
var CREDENTIAL_TYPES = ["face", "card", "qr", "pin", "plate", "app"];
var CREDENTIAL_TYPE_LABELS = {
  face: "Face",
  card: "Cart\xE3o",
  qr: "QR Code",
  pin: "Senha num\xE9rica",
  plate: "Placa",
  app: "Aplicativo"
};
var MANUAL_CREDENTIAL_TYPES = ["card", "pin", "qr", "plate", "app"];
var CREDENTIAL_STATUSES = ["active", "suspended", "revoked"];
var CREDENTIAL_STATUS_LABELS = {
  active: "Ativa",
  suspended: "Suspensa",
  revoked: "Revogada"
};
var WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "S\xE1b"];
var trimmed = (max, required) => {
  const base = z.string({ error: required ?? "Valor inv\xE1lido" }).trim().max(max, `M\xE1ximo de ${max} caracteres`);
  return required ? base.min(1, required) : base;
};
var nullable = (schema) => z.preprocess((v) => v === "" || v === void 0 ? null : v, schema.nullable());
var cpfSchema = z.string({ error: "Informe o CPF" }).transform(normalizeCpf).refine(isValidCpf, "CPF inv\xE1lido");
var cnpjSchema = z.string({ error: "Informe o CNPJ" }).transform(normalizeCnpj).refine(isValidCnpj, "CNPJ inv\xE1lido");
var phoneInputSchema = z.string({ error: "Informe o celular" }).trim().transform((v, ctx) => {
  const normalized = normalizePhoneBR(v);
  if (!normalized) {
    ctx.addIssue({ code: "custom", message: "Informe um celular v\xE1lido com DDD" });
    return z.NEVER;
  }
  return normalized;
});
var emailInputSchema = z.email({ error: "E-mail inv\xE1lido" }).trim().toLowerCase().max(160);
var plateSchema = z.string({ error: "Informe a placa" }).transform(normalizePlate).refine((v) => plateFormatOf(v) !== null, "Placa inv\xE1lida (use AAA1234 ou AAA1B23)");
var timezoneSchema = z.string().trim().refine(isValidTimezone, "Fuso hor\xE1rio inv\xE1lido");
var scheduleSchema = z.object({
  days: z.array(z.number().int().min(0).max(6)).min(1, "Escolha ao menos um dia"),
  from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora inv\xE1lida"),
  to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora inv\xE1lida")
}).refine((s) => s.from < s.to, {
  message: "O hor\xE1rio final deve ser depois do inicial",
  path: ["to"]
});
var organizationSchema = z.object({
  name: trimmed(160, "Informe o nome da administradora"),
  cnpj: nullable(cnpjSchema).optional(),
  email: nullable(emailInputSchema).optional(),
  phone: nullable(phoneInputSchema).optional()
});
var organizationUpdateSchema = updateSchema(organizationSchema).extend({
  isActive: z.boolean().optional()
});
var condominiumSchema = z.object({
  name: trimmed(160, "Informe o nome do condom\xEDnio"),
  cnpj: nullable(cnpjSchema).optional(),
  addressLine: nullable(trimmed(200)).optional(),
  addressNumber: nullable(trimmed(20)).optional(),
  district: nullable(trimmed(100)).optional(),
  city: nullable(trimmed(100)).optional(),
  state: nullable(z.string().trim().toUpperCase().length(2, "UF com 2 letras")).optional(),
  zipCode: nullable(
    z.string().transform((v) => v.replace(/\D/g, "")).refine((v) => v.length === 8, "CEP com 8 d\xEDgitos")
  ).optional(),
  timezone: timezoneSchema.optional(),
  email: nullable(emailInputSchema).optional(),
  phone: nullable(phoneInputSchema).optional(),
  logoKey: nullable(z.string().max(300)).optional(),
  settings: z.object({
    modules: z.record(z.string(), z.boolean()).optional(),
    /** False = the condominium has no blocks/towers: units are just numbers (ADR-098). */
    blocks: z.boolean().optional(),
    /** What each unit link role may do in the resident app (ADR-102); missing = defaults. */
    residentFeatures: z.partialRecord(
      z.enum(UNIT_PERSON_ROLES),
      z.partialRecord(z.enum(RESIDENT_FEATURES), z.boolean())
    ).optional()
  }).optional()
});
var condominiumCreateSchema = condominiumSchema.extend({
  /** Required for the platform operator; ignored for org_admin (their own organization). */
  organizationId: z.uuid().optional()
});
var blockSchema = z.object({
  name: trimmed(60, "Informe o nome do bloco"),
  code: nullable(trimmed(10)).optional(),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional()
});
var unitSchema = z.object({
  /** Required while the condominium has blocks; without them the single block is implied. */
  blockId: z.uuid({ error: "Selecione o bloco" }).optional(),
  number: trimmed(20, "Informe o n\xFAmero da unidade"),
  floor: nullable(trimmed(20)).optional(),
  notes: nullable(trimmed(500)).optional(),
  isActive: z.boolean().optional()
});
var unitsImportOptionsSchema = z.object({
  /** Validate only; write nothing. */
  dryRun: z.union([z.boolean(), z.enum(["true", "false", "1", "0"])]).transform((v) => v === true || v === "true" || v === "1").default(false)
});
var personSchema = z.object({
  fullName: trimmed(160, "Informe o nome completo").min(3, "Informe o nome completo"),
  cpf: nullable(cpfSchema).optional(),
  email: nullable(emailInputSchema).optional(),
  phone: nullable(phoneInputSchema).optional(),
  birthDate: nullable(z.iso.date({ error: "Data inv\xE1lida" })).optional(),
  notes: nullable(trimmed(1e3)).optional(),
  photoKey: nullable(z.string().max(300)).optional(),
  isActive: z.boolean().optional(),
  /**
   * Link to a unit in the same save (ADR-093): what makes the person exist on the terminals.
   * Ignored on updates; links are edited on the unit page.
   */
  unitLink: z.object({
    unitId: z.uuid({ error: "Selecione a unidade" }),
    role: z.enum(UNIT_PERSON_ROLES, { error: "Selecione o tipo de v\xEDnculo" }),
    isResponsible: z.boolean().optional()
  }).nullable().optional()
});
var unitPersonSchema = z.object({
  personId: z.uuid({ error: "Selecione a pessoa" }),
  role: z.enum(UNIT_PERSON_ROLES, { error: "Selecione o tipo de v\xEDnculo" }),
  isResponsible: z.boolean().default(false),
  validFrom: z.iso.datetime({ offset: true }).optional(),
  validUntil: nullable(z.iso.datetime({ offset: true })).optional(),
  authorizationKind: nullable(z.enum(AUTHORIZATION_KINDS)).optional(),
  schedule: nullable(scheduleSchema).optional(),
  notes: nullable(trimmed(500)).optional()
}).superRefine((v, ctx) => {
  if (v.role === "autorizado" && !v.authorizationKind) {
    ctx.addIssue({
      code: "custom",
      path: ["authorizationKind"],
      message: "Informe o tipo de autoriza\xE7\xE3o"
    });
  }
});
var unitPersonUpdateSchema = z.object({
  role: z.enum(UNIT_PERSON_ROLES).optional(),
  isResponsible: z.boolean().optional(),
  validUntil: nullable(z.iso.datetime({ offset: true })).optional(),
  authorizationKind: nullable(z.enum(AUTHORIZATION_KINDS)).optional(),
  schedule: nullable(scheduleSchema).optional(),
  notes: nullable(trimmed(500)).optional()
});
var endLinkSchema = z.object({
  reason: nullable(trimmed(300)).optional()
});
var vehicleSchema = z.object({
  plate: plateSchema,
  brand: nullable(trimmed(60)).optional(),
  model: nullable(trimmed(60)).optional(),
  color: nullable(trimmed(30)).optional(),
  unitId: nullable(z.uuid()).optional(),
  personId: nullable(z.uuid()).optional(),
  notes: nullable(trimmed(300)).optional(),
  isActive: z.boolean().optional()
});
var parkingSpaceSchema = z.object({
  code: trimmed(20, "Informe o c\xF3digo da vaga"),
  blockId: nullable(z.uuid()).optional(),
  unitId: nullable(z.uuid()).optional(),
  vehicleId: nullable(z.uuid()).optional(),
  notes: nullable(trimmed(300)).optional()
});
var credentialCreateSchema = z.object({
  type: z.enum(MANUAL_CREDENTIAL_TYPES, { error: "Selecione o tipo" }),
  identifier: nullable(trimmed(64)).optional(),
  pin: nullable(z.string().regex(/^\d{4,8}$/, "Senha num\xE9rica de 4 a 8 d\xEDgitos")).optional(),
  vehicleId: nullable(z.uuid()).optional(),
  label: nullable(trimmed(60)).optional(),
  validFrom: z.iso.datetime({ offset: true }).optional(),
  validUntil: nullable(z.iso.datetime({ offset: true })).optional()
}).superRefine((v, ctx) => {
  if (v.type === "card" && !v.identifier) {
    ctx.addIssue({ code: "custom", path: ["identifier"], message: "Informe o n\xFAmero do cart\xE3o" });
  }
  if (v.type === "pin" && !v.pin) {
    ctx.addIssue({ code: "custom", path: ["pin"], message: "Informe a senha num\xE9rica" });
  }
  if (v.type === "plate" && !v.vehicleId && !v.identifier) {
    ctx.addIssue({
      code: "custom",
      path: ["vehicleId"],
      message: "Selecione o ve\xEDculo ou informe a placa"
    });
  }
});
var credentialUpdateSchema = z.object({
  status: z.enum(["active", "suspended"]).optional(),
  label: nullable(trimmed(60)).optional(),
  validUntil: nullable(z.iso.datetime({ offset: true })).optional()
});
var accessPointSchema = z.object({
  name: trimmed(80, "Informe o nome do ponto de acesso"),
  type: z.enum(ACCESS_POINT_TYPES, { error: "Selecione o tipo" }),
  direction: z.enum(ACCESS_DIRECTIONS).default("entrada"),
  deviceId: nullable(z.uuid()).optional(),
  cameraId: nullable(z.uuid()).optional(),
  relayOutput: nullable(z.coerce.number().int().min(1).max(32)).optional(),
  openDurationMs: z.coerce.number().int().min(500, "M\xEDnimo 500 ms").max(6e4, "M\xE1ximo 60 s").default(3e3),
  isActive: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0)
});
var cameraBaseSchema = z.object({
  name: trimmed(80, "Informe o nome da c\xE2mera"),
  sourceType: z.enum(CAMERA_SOURCES).default("rtsp"),
  rtspUrl: nullable(z.string().trim().max(500)).optional(),
  subStreamUrl: nullable(z.string().trim().max(500)).optional(),
  deviceId: nullable(z.uuid()).optional(),
  nvrChannel: nullable(z.coerce.number().int().min(1).max(256)).optional(),
  location: nullable(trimmed(120)).optional(),
  /** Front-desk staff (porteiro) only see cameras flagged here; managers see every camera. */
  visibleToFrontDesk: z.boolean().default(true),
  /** Residents may watch it in the app (common areas). Door cameras are always visible to who can open the door. */
  visibleToResidents: z.boolean().default(false),
  isActive: z.boolean().default(true)
});
function checkCameraUrls(v, ctx) {
  for (const field of ["rtspUrl", "subStreamUrl"]) {
    const value = v[field];
    if (value && !/^rtsps?:\/\//i.test(value)) {
      ctx.addIssue({ code: "custom", path: [field], message: "A URL deve come\xE7ar com rtsp://" });
    }
  }
}
var cameraSchema = cameraBaseSchema.superRefine((v, ctx) => {
  checkCameraUrls(v, ctx);
  if (v.sourceType === "rtsp" && !v.rtspUrl) {
    ctx.addIssue({ code: "custom", path: ["rtspUrl"], message: "Informe a URL RTSP" });
  }
  if (v.sourceType === "nvr_channel" && !v.nvrChannel) {
    ctx.addIssue({ code: "custom", path: ["nvrChannel"], message: "Informe o canal do NVR" });
  }
});
var cameraUpdateSchema = updateSchema(cameraBaseSchema).superRefine(checkCameraUrls);
var UPLOAD_KINDS = [
  "person_photo",
  "condominium_logo",
  "face",
  "visitor_photo",
  "package_photo",
  "announcement_file"
];
var UPLOAD_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
var UPLOAD_FILE_MAX_BYTES = 10 * 1024 * 1024;
var presignUploadSchema = z.object({
  kind: z.enum(UPLOAD_KINDS),
  contentType: z.enum(["image/jpeg", "image/png", "image/webp", "application/pdf"], {
    error: "Envie uma imagem JPG, PNG ou WebP"
  }),
  size: z.number().int().min(1).max(UPLOAD_FILE_MAX_BYTES, "Arquivo de at\xE9 10 MB")
}).superRefine((value, ctx) => {
  const file = value.kind === "announcement_file";
  if (value.contentType === "application/pdf" && !file) {
    ctx.addIssue({
      code: "custom",
      path: ["contentType"],
      message: "Envie uma imagem JPG, PNG ou WebP"
    });
  }
  if (!file && value.size > UPLOAD_IMAGE_MAX_BYTES) {
    ctx.addIssue({ code: "custom", path: ["size"], message: "Imagem de at\xE9 5 MB" });
  }
});
var DEVICE_TYPE_LABELS = {
  idface: "Terminal facial (iDFace)",
  nvr: "NVR",
  camera: "C\xE2mera IP",
  relay: "Placa de rel\xE9s"
};

// src/access.ts
var ACCESS_RULE_KINDS = ["residents", "staff", "providers", "custom"];
var ACCESS_RULE_KIND_LABELS = {
  residents: "Moradores",
  staff: "Funcion\xE1rios",
  providers: "Prestadores e autorizados",
  custom: "Personalizado"
};
var accessRuleSchema = z.object({
  name: z.string({ error: "Informe o nome" }).trim().min(1, "Informe o nome").max(80),
  description: z.string().trim().max(300).nullable().optional(),
  kind: z.enum(ACCESS_RULE_KINDS, { error: "Selecione o tipo" }),
  /** People whose active unit link has one of these roles are members automatically. */
  roles: z.array(z.enum(UNIT_PERSON_ROLES)).default([]),
  /** Access points covered by the rule (empty = every active point). */
  accessPointIds: z.array(z.uuid()).default([]),
  /** Time windows (empty = 24 h). */
  schedules: z.array(scheduleSchema).max(10).default([]),
  blockOnHolidays: z.boolean().default(false),
  validFrom: z.iso.datetime({ offset: true }).nullable().optional(),
  validUntil: z.iso.datetime({ offset: true }).nullable().optional(),
  isActive: z.boolean().default(true)
});
var accessRuleUpdateSchema = updateSchema(accessRuleSchema);
var accessRuleMembersSchema = z.object({
  personIds: z.array(z.uuid()).max(500)
});
var INDIVIDUAL_RULE_PREFIX = "Libera\xE7\xE3o individual \xB7 ";
var personAccessSchema = z.object({
  accessPointIds: z.array(z.uuid()).max(200),
  cameraIds: z.array(z.uuid()).max(200)
});
var holidaySchema = z.object({
  date: z.iso.date({ error: "Informe a data" }),
  name: z.string({ error: "Informe o nome" }).trim().min(1, "Informe o nome").max(80),
  /** Repeats every year on the same day/month. */
  recurring: z.boolean().default(false)
});
var openDoorSchema = z.object({
  reason: z.string({ error: "Informe o motivo" }).trim().min(3, "Descreva o motivo (m\xEDnimo 3 caracteres)").max(200)
});
var BIOMETRIC_CONSENT = {
  type: "biometric",
  version: "2026-09.v1",
  title: "Consentimento para uso de biometria facial",
  text: [
    "Autorizo o condom\xEDnio a coletar e armazenar minha imagem facial com a finalidade exclusiva de controle de acesso \xE0s \xE1reas do condom\xEDnio.",
    "A imagem \xE9 armazenada de forma criptografada e enviada apenas aos terminais de acesso autorizados deste condom\xEDnio.",
    "Posso revogar este consentimento a qualquer momento, pedindo a exclus\xE3o da biometria; nesse caso ela \xE9 removida dos terminais e o acesso passa a ser feito por cart\xE3o, QR code ou senha.",
    "Tratamento com base no art. 11, I, da LGPD (Lei 13.709/2018)."
  ].join("\n")
};
var CONSENT_SIGNERS = ["person", "guardian", "manager"];
var CONSENT_SIGNER_LABELS = {
  person: "A pr\xF3pria pessoa",
  guardian: "Respons\xE1vel legal",
  manager: "Gestor do condom\xEDnio (presencial)"
};
var faceEnrollSchema = z.object({
  /** Key returned by POST /uploads/presign (kind `face`). */
  objectKey: z.string().trim().min(1, "Envie a foto primeiro"),
  consent: z.object({
    accepted: z.literal(true, { error: "\xC9 preciso aceitar o termo de consentimento" }),
    termVersion: z.literal(BIOMETRIC_CONSENT.version),
    signedBy: z.enum(CONSENT_SIGNERS).default("person")
  })
});
var FACE_STATUS = ["pending", "synced", "partial", "error"];
var FACE_STATUS_LABELS = {
  pending: "Enviando aos terminais",
  synced: "Sincronizada",
  partial: "Parcialmente sincronizada",
  error: "Erro em terminal"
};
var SYNC_STATES = ["pending", "synced", "error"];
var SYNC_STATE_LABELS = {
  pending: "Pendente",
  synced: "Sincronizado",
  error: "Erro"
};
var ACCESS_RESULTS = ["granted", "denied"];
var ACCESS_EVENT_KINDS = ["access", "door"];
var ACCESS_ORIGINS = ["terminal", "online", "web", "app", "portaria", "call"];
var ACCESS_ORIGIN_LABELS = {
  terminal: "Terminal",
  online: "Gateway (modo online)",
  web: "Portal",
  app: "Aplicativo",
  portaria: "Portaria",
  call: "Chamada"
};
var accessEventsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  personId: z.uuid().optional(),
  unitId: z.uuid().optional(),
  accessPointId: z.uuid().optional(),
  result: z.enum(ACCESS_RESULTS).optional(),
  kind: z.enum(ACCESS_EVENT_KINDS).optional(),
  search: z.string().trim().max(100).optional()
});
var LIVE_NAMESPACE = "/live";
var LIVE_EVENTS = {
  access: "access.event",
  alert: "alert.changed",
  sync: "sync.changed",
  /** Intercom calls of the condominium (front desk screen, totem status). */
  call: "call.changed",
  /** A package was registered, handed over or returned (desk screens refresh). */
  package: "package.changed",
  /** An announcement was published, edited or archived (the desk wall refreshes). */
  announcement: "announcement.changed"
};

// src/api.ts
var API_PREFIX = "/api/v1";
var CLIENT_TYPE_HEADER = "x-client-type";
var auditQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  /** Exact action (`unit.update`) or a prefix ending in `.` or `*` (`camera.`, `auth.*`). */
  action: z.string().trim().max(80).optional(),
  entityType: z.string().trim().max(60).optional(),
  entityId: z.uuid().optional(),
  actorUserId: z.uuid().optional(),
  /** Free text matched against the actor's name or e-mail. */
  q: z.string().trim().max(100).optional()
});
var AUDIT_ENTITY_LABELS = {
  auth: "Autentica\xE7\xE3o",
  user: "Usu\xE1rio",
  organization: "Administradora",
  condominium: "Condom\xEDnio",
  block: "Bloco",
  unit: "Unidade",
  units: "Unidades",
  unit_person: "V\xEDnculo",
  person: "Pessoa",
  vehicle: "Ve\xEDculo",
  parking_space: "Vaga",
  credential: "Credencial",
  face: "Biometria facial",
  consent: "Consentimento",
  invite: "Convite",
  access_point: "Ponto de acesso",
  access_rule: "Regra de acesso",
  holiday: "Feriado",
  door: "Porta",
  camera: "C\xE2mera",
  device: "Dispositivo",
  gateway: "Gateway",
  sync: "Sincroniza\xE7\xE3o",
  alert: "Alerta",
  call: "Chamada",
  intercom_station: "Totem",
  frontdesk: "Portaria",
  visitor: "Visitante",
  visitor_invite: "Convite de visitante",
  package: "Encomenda",
  announcement: "Aviso",
  charge: "Cobran\xE7a",
  session: "Sess\xE3o",
  frontdesk_shift: "Plant\xE3o",
  visitor_entry: "Entrada de visitante",
  agent_release: "Release do agente",
  superadmin: "Operador",
  seed: "Demonstra\xE7\xE3o"
};
var AUDIT_VERB_LABELS = {
  created: "cria\xE7\xE3o",
  updated: "atualiza\xE7\xE3o",
  deleted: "exclus\xE3o",
  deactivated: "desativa\xE7\xE3o",
  ended: "encerramento",
  imported: "importa\xE7\xE3o",
  members_updated: "membros atualizados",
  revoked: "revoga\xE7\xE3o",
  resent: "reenvio",
  accepted: "aceite",
  enrolled_by_app: "cadastro pelo app",
  removed: "exclus\xE3o",
  view: "visualiza\xE7\xE3o ao vivo",
  snapshot: "snapshot",
  playback: "reprodu\xE7\xE3o",
  recordings_search: "busca de grava\xE7\xF5es",
  open: "abertura",
  opened: "abertura",
  held_open: "mantida aberta",
  closed: "fechada",
  login: "entrada",
  login_failed: "falha de login",
  logout: "sa\xEDda",
  logout_all: "sa\xEDda de todos os dispositivos",
  mfa_challenge: "desafio 2FA",
  mfa_verified: "2FA verificado",
  mfa_failed: "falha no 2FA",
  mfa_enabled: "2FA ativado",
  mfa_disabled: "2FA desativado",
  password_changed: "senha alterada",
  password_reset: "senha redefinida",
  password_reset_requested: "redefini\xE7\xE3o pedida",
  password_reset_requested_unknown: "redefini\xE7\xE3o para conta desconhecida",
  password_reset_throttled: "redefini\xE7\xE3o limitada",
  refresh_reuse_detected: "reuso de token detectado",
  session_revoked: "sess\xE3o revogada",
  switch_condominium: "troca de condom\xEDnio",
  switch_condominium_denied: "troca de condom\xEDnio negada",
  token_rotated: "token renovado",
  install_script_served: "script de instala\xE7\xE3o entregue",
  online: "online",
  offline: "offline",
  agent_updated: "agente atualizado",
  registered: "registro",
  resolved: "resolvido",
  started: "in\xEDcio",
  answered: "atendida",
  declined: "recusada",
  finished: "finalizada",
  shift_started: "plant\xE3o iniciado",
  shift_ended: "plant\xE3o encerrado",
  resync_all: "ressincroniza\xE7\xE3o completa",
  profile_updated: "perfil atualizado",
  notification_preferences_updated: "prefer\xEAncias de notifica\xE7\xE3o",
  request: "requisi\xE7\xE3o",
  demo: "dados de demonstra\xE7\xE3o",
  checkin: "entrada registrada",
  arrived: "chegada",
  received: "recebimento",
  picked_up: "retirada",
  pickup_code_failed: "c\xF3digo de retirada incorreto",
  returned: "devolu\xE7\xE3o",
  pickup_authorized: "retirada autorizada",
  pickup_authorization_removed: "autoriza\xE7\xE3o de retirada removida",
  published: "publica\xE7\xE3o",
  archived: "arquivamento",
  access_updated: "libera\xE7\xF5es atualizadas",
  erased: "dados exclu\xEDdos",
  batch_created: "lote criado",
  canceled: "cancelamento",
  paid: "baixa",
  reopened: "reabertura",
  pruned: "remo\xE7\xE3o autom\xE1tica"
};
function auditActionLabel(action) {
  const dot = action.indexOf(".");
  if (dot === -1) return action;
  const entity = action.slice(0, dot);
  const verb = action.slice(dot + 1);
  if (entity === "gateway" && verb.startsWith("command.")) {
    return `Gateway \xB7 comando ${verb.slice("command.".length)}`;
  }
  const entityLabel = AUDIT_ENTITY_LABELS[entity] ?? entity;
  const verbLabel = AUDIT_VERB_LABELS[verb] ?? verb.replace(/_/g, " ");
  return `${entityLabel} \xB7 ${verbLabel}`;
}
var loginSchema = z.object({
  /** E-mail or mobile phone number. */
  identifier: z.string({ error: "Informe seu e-mail ou celular" }).trim().min(3, "Informe seu e-mail ou celular").max(160, "Identificador muito longo"),
  password: z.string({ error: "Informe sua senha" }).min(1, "Informe sua senha").max(200, "Senha muito longa"),
  /** Friendly device name shown in the session list ("Chrome no Windows"). */
  deviceName: z.string().trim().max(120).optional()
});
var refreshSchema = z.object({
  /** Optional when the web client sends the refresh token as an httpOnly cookie. */
  refreshToken: z.string().min(20).max(1e3).optional()
});
var switchCondominiumSchema = z.object({
  condominiumId: z.uuid({ error: "Condom\xEDnio inv\xE1lido" })
});
var passwordSchema = z.string({ error: "Informe uma senha" }).min(8, "A senha precisa ter pelo menos 8 caracteres").max(200, "Senha muito longa").refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), "Use letras e n\xFAmeros na senha");
var PROTOCOL_VERSION = 1;
var topics = {
  status: (tenantId, gatewayId) => `t/${tenantId}/gw/${gatewayId}/status`,
  cmd: (tenantId, gatewayId) => `t/${tenantId}/gw/${gatewayId}/cmd`,
  evt: (tenantId, gatewayId) => `t/${tenantId}/gw/${gatewayId}/evt`,
  ack: (tenantId, gatewayId) => `t/${tenantId}/gw/${gatewayId}/ack`
};
var HEARTBEAT_INTERVAL_SECONDS = 30;
var OFFLINE_AFTER_SECONDS = 90;
var COMMAND_TYPES = [
  "open_door",
  "sync_now",
  "enroll_face",
  "remove_face",
  "revoke_credential",
  "device_test",
  "discover_channels",
  "get_snapshot",
  "restart_driver",
  "update_agent",
  "get_logs",
  "search_recordings",
  "prepare_stream",
  "net_probe",
  "webrtc_offer"
];
var EVENT_TYPES = [
  "heartbeat",
  "cmd.result",
  "device.online",
  "device.offline",
  "sync.done",
  "access.granted",
  "access.denied",
  "door.opened",
  "door.held_open",
  "door.closed",
  "agent.updated",
  "camera.online",
  "camera.offline"
];
var DEVICE_STATUS = ["unknown", "online", "offline", "error"];
var AGENT_PLATFORMS = ["linux/amd64", "linux/arm64", "linux/arm"];
var isoDate = z.iso.datetime({ offset: true });
var uuid = z.uuid();
var envelopeSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  id: uuid,
  seq: z.number().int().min(0),
  ts: isoDate,
  type: z.string().min(1),
  payload: z.record(z.string(), z.unknown())
});
var deviceHealthSchema = z.object({
  id: uuid,
  status: z.enum(DEVICE_STATUS),
  lastSeenAt: isoDate.nullable(),
  error: z.string().nullable()
});
var gatewayStatusSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  gatewayId: uuid,
  online: z.boolean(),
  ts: isoDate,
  agentVersion: z.string(),
  platform: z.string(),
  hostname: z.string(),
  startedAt: isoDate,
  uptimeSeconds: z.number().int().min(0),
  mock: z.boolean(),
  syncVersion: z.number().int().min(0),
  devices: z.array(deviceHealthSchema),
  clockOffsetMs: z.number().int().nullable()
});
var ackSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  ids: z.array(uuid).min(1)
});
var openDoorCommand = z.object({
  accessPointId: uuid,
  deviceId: uuid.nullable(),
  relayOutput: z.number().int().nullable(),
  durationMs: z.number().int().min(100).max(6e4),
  reason: z.string().nullable(),
  requestedBy: z.string().nullable(),
  /** Where the request came from (web portal, front desk, resident app, intercom call). */
  origin: z.enum(["web", "portaria", "app", "call"]).optional()
});
var syncNowCommand = z.object({
  reason: z.string().nullable(),
  full: z.boolean()
});
var enrollFaceCommand = z.object({
  personId: uuid,
  credentialId: uuid,
  /** Authenticated cloud URL of the (decrypted) JPEG; the gateway adds its bearer token. */
  imageUrl: z.string().url(),
  /** Changes whenever the image changes (updatedAt of the enrollment). */
  faceVersion: z.string(),
  /** Empty = every face terminal of the gateway. */
  deviceIds: z.array(uuid)
});
var removeFaceCommand = z.object({
  personId: uuid,
  credentialId: uuid,
  deviceIds: z.array(uuid)
});
var revokeCredentialCommand = z.object({
  personId: uuid,
  credentialIds: z.array(uuid),
  reason: z.string().nullable()
});
var deviceTestCommand = z.object({ deviceId: uuid });
var discoverChannelsCommand = z.object({ deviceId: uuid });
var getSnapshotCommand = z.object({
  cameraId: uuid,
  /** Optional signed PUT; when null the agent uses POST /gateway/photos and answers `{ key }`. */
  uploadUrl: z.string().url().nullable()
});
var searchRecordingsCommand = z.object({
  cameraId: uuid,
  from: isoDate,
  to: isoDate
});
var prepareStreamCommand = z.object({
  name: z.string().regex(/^[a-z0-9_]{3,64}$/),
  cameraId: uuid,
  /** Playback window; null = live stream. */
  from: isoDate.nullable(),
  to: isoDate.nullable(),
  ttlSeconds: z.number().int().min(30).max(86400)
});
var restartDriverCommand = z.object({ deviceId: uuid.nullable() });
var updateAgentCommand = z.object({
  version: z.string(),
  platform: z.enum(AGENT_PLATFORMS),
  url: z.string().url(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  signature: z.string()
});
var getLogsCommand = z.object({ lines: z.number().int().min(1).max(2e3) });
var NET_PROBE_HOST_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9.:-]{0,251}[A-Za-z0-9])?$/;
var netProbeCommand = z.object({
  host: z.string().trim().min(1).max(253).regex(NET_PROBE_HOST_PATTERN),
  /** TCP port to try after the ping; null = ping only. */
  port: z.number().int().min(1).max(65535).nullable()
});
var netProbePing = z.object({
  sent: z.number().int().min(0),
  received: z.number().int().min(0),
  lossPercent: z.number().min(0).max(100),
  minMs: z.number().min(0).nullable(),
  avgMs: z.number().min(0).nullable(),
  maxMs: z.number().min(0).nullable()
});
var netProbeTcp = z.object({
  port: z.number().int().min(1).max(65535),
  open: z.boolean(),
  /** Why the connect failed (connection refused, timeout…); null when open. */
  error: z.string().nullable()
});
var netProbeResult = z.object({
  host: z.string(),
  resolvedIp: z.string().nullable(),
  /** null when ping could not run on the gateway (binary missing). */
  ping: netProbePing.nullable(),
  tcp: netProbeTcp.nullable()
});
var webrtcOfferCommand = z.object({
  cameraId: uuid,
  sdp: z.string().min(1),
  quality: z.enum(["sd", "hd"])
});
var webrtcOfferResult = z.object({ sdp: z.string().min(1) });
var commandSchemas = {
  open_door: openDoorCommand,
  sync_now: syncNowCommand,
  enroll_face: enrollFaceCommand,
  remove_face: removeFaceCommand,
  revoke_credential: revokeCredentialCommand,
  device_test: deviceTestCommand,
  discover_channels: discoverChannelsCommand,
  get_snapshot: getSnapshotCommand,
  restart_driver: restartDriverCommand,
  update_agent: updateAgentCommand,
  get_logs: getLogsCommand,
  search_recordings: searchRecordingsCommand,
  prepare_stream: prepareStreamCommand,
  net_probe: netProbeCommand,
  webrtc_offer: webrtcOfferCommand
};
var commandErrorSchema = z.object({
  code: z.string(),
  message: z.string()
});
var commandResultEvent = z.object({
  commandId: uuid,
  commandType: z.enum(COMMAND_TYPES),
  ok: z.boolean(),
  durationMs: z.number().int().min(0),
  error: commandErrorSchema.nullable(),
  result: z.record(z.string(), z.unknown()).nullable()
});
var heartbeatEvent = z.object({
  uptimeSeconds: z.number().int().min(0),
  agentVersion: z.string(),
  syncVersion: z.number().int().min(0),
  devices: z.array(deviceHealthSchema),
  outboxPending: z.number().int().min(0)
});
var deviceStateEvent = z.object({
  deviceId: uuid,
  status: z.enum(DEVICE_STATUS),
  error: z.string().nullable(),
  deviceTs: isoDate.nullable()
});
var syncDoneEvent = z.object({
  version: z.number().int().min(0),
  full: z.boolean(),
  applied: z.object({
    persons: z.number().int().min(0),
    credentials: z.number().int().min(0),
    devices: z.number().int().min(0),
    cameras: z.number().int().min(0),
    accessPoints: z.number().int().min(0)
  }),
  errors: z.array(
    z.object({ deviceId: uuid.nullable(), personId: uuid.nullable(), message: z.string() })
  ),
  /**
   * Per person/device outcome of the terminals that hold people (iDFace). `pending` = held back
   * on purpose (the person is not active on the terminal yet: no unit link), with the reason.
   */
  results: z.array(
    z.object({
      deviceId: uuid,
      personId: uuid,
      status: z.enum(["synced", "error", "pending"]),
      message: z.string().nullable()
    })
  )
});
var accessEvent = z.object({
  deviceId: uuid,
  accessPointId: uuid.nullable(),
  personId: uuid.nullable(),
  credentialId: uuid.nullable(),
  credentialType: z.string().nullable(),
  identifier: z.string().nullable(),
  reason: z.string().nullable(),
  deviceTs: isoDate,
  /** Object key returned by POST /gateway/photos (terminal photo of the read). */
  terminalPhotoKey: z.string().nullable(),
  /** Object key of the snapshot of the camera linked to the access point, when any. */
  cameraSnapshotKey: z.string().nullable(),
  /** Id of the log entry on the terminal (deduplication across polling and push). */
  deviceEventId: z.string().nullable(),
  /** `terminal` = decided by the device, `online` = decided by the gateway (online mode). */
  origin: z.enum(["terminal", "online"]),
  /** Visitor invite behind a `CV1.` QR code (validated by the gateway), when any. */
  visitorInviteId: uuid.nullable().optional(),
  visitorName: z.string().nullable().optional()
});
var doorEvent = z.object({
  accessPointId: uuid.nullable(),
  deviceId: uuid.nullable(),
  origin: z.string(),
  commandId: uuid.nullable(),
  deviceTs: isoDate
});
var cameraStateEvent = z.object({
  cameraId: uuid,
  status: z.enum(["online", "offline"]),
  error: z.string().nullable(),
  deviceTs: isoDate
});
var agentUpdatedEvent = z.object({
  fromVersion: z.string(),
  toVersion: z.string(),
  ok: z.boolean(),
  error: z.string().nullable()
});
var eventSchemas = {
  heartbeat: heartbeatEvent,
  "cmd.result": commandResultEvent,
  "device.online": deviceStateEvent,
  "device.offline": deviceStateEvent,
  "sync.done": syncDoneEvent,
  "access.granted": accessEvent,
  "access.denied": accessEvent,
  "door.opened": doorEvent,
  "door.held_open": doorEvent,
  "door.closed": doorEvent,
  "agent.updated": agentUpdatedEvent,
  "camera.online": cameraStateEvent,
  "camera.offline": cameraStateEvent
};
var syncPersonSchema = z.object({
  id: uuid,
  fullName: z.string(),
  active: z.boolean(),
  unitLabels: z.array(z.string()),
  /** iso datetime after which access is denied, if any */
  validUntil: isoDate.nullable(),
  /** Access rules (groups) this person belongs to, by role or explicit membership. */
  ruleIds: z.array(uuid),
  /** Per-link schedule restriction, if the tightest link has one ({days, from, to}). */
  schedule: z.object({ days: z.array(z.number().int().min(0).max(6)), from: z.string(), to: z.string() }).nullable(),
  updatedAt: isoDate
});
var syncScheduleSchema = z.object({
  days: z.array(z.number().int().min(0).max(6)),
  from: z.string(),
  to: z.string()
});
var syncAccessRuleSchema = z.object({
  id: uuid,
  name: z.string(),
  /** Empty = every access point of the condominium. */
  accessPointIds: z.array(uuid),
  /** Empty = 24 h. */
  schedules: z.array(syncScheduleSchema),
  blockOnHolidays: z.boolean(),
  validFrom: isoDate.nullable(),
  validUntil: isoDate.nullable(),
  active: z.boolean(),
  updatedAt: isoDate
});
var syncCredentialSchema = z.object({
  id: uuid,
  personId: uuid,
  type: z.enum(["face", "card", "qr", "pin", "plate", "app"]),
  identifier: z.string().nullable(),
  /** PIN in clear (delivered only through the tunnel to the terminals that need it). */
  secret: z.string().nullable(),
  status: z.enum(["active", "suspended", "revoked"]),
  validFrom: isoDate,
  validUntil: isoDate.nullable(),
  /** For `face`: version of the enrolled image (re-download when it changes). */
  faceVersion: z.string().nullable(),
  updatedAt: isoDate
});
var syncDeviceSchema = z.object({
  id: uuid,
  name: z.string(),
  type: z.enum(["idface", "nvr", "camera", "relay"]),
  driver: z.string().nullable(),
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  /** Decrypted credentials JSON for the driver ({ username, password } ...). */
  credentials: z.record(z.string(), z.unknown()).nullable(),
  settings: z.record(z.string(), z.unknown()),
  active: z.boolean(),
  updatedAt: isoDate
});
var syncCameraSchema = z.object({
  id: uuid,
  name: z.string(),
  sourceType: z.enum(["rtsp", "nvr_channel", "onvif"]),
  deviceId: uuid.nullable(),
  nvrChannel: z.number().int().nullable(),
  rtspUrl: z.string().nullable(),
  subStreamUrl: z.string().nullable(),
  active: z.boolean(),
  updatedAt: isoDate
});
var syncAccessPointSchema = z.object({
  id: uuid,
  name: z.string(),
  type: z.enum(["portao_social", "portao_garagem", "porta", "catraca"]),
  direction: z.enum(["entrada", "saida", "ambos"]),
  deviceId: uuid.nullable(),
  cameraId: uuid.nullable(),
  relayOutput: z.number().int().nullable(),
  openDurationMs: z.number().int(),
  active: z.boolean(),
  updatedAt: isoDate
});
var syncVisitorInviteSchema = z.object({
  id: uuid,
  unitId: uuid,
  unitLabel: z.string(),
  hostPersonId: uuid.nullable(),
  hostName: z.string().nullable(),
  visitorName: z.string(),
  validFrom: isoDate,
  validUntil: isoDate,
  /** Empty = any access point with a terminal. */
  accessPointIds: z.array(uuid),
  singleUse: z.boolean(),
  usedCount: z.number().int().min(0),
  status: z.enum(["active", "used", "revoked"]),
  updatedAt: isoDate,
  /** Signed QR payload (`CV1.<id>.<signature>`) the terminal enrolls for local reads (ADR-105); null when the cloud has no key. */
  qr: z.string().nullable().optional()
});
var syncResponseSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  gatewayId: uuid,
  tenantId: uuid,
  /** Monotonic version (epoch milliseconds of the last change). */
  version: z.number().int().min(0),
  since: z.number().int().min(0),
  /** True when everything is included (first sync or gateway too far behind). */
  full: z.boolean(),
  persons: z.array(syncPersonSchema),
  credentials: z.array(syncCredentialSchema),
  devices: z.array(syncDeviceSchema),
  cameras: z.array(syncCameraSchema),
  accessPoints: z.array(syncAccessPointSchema),
  accessRules: z.array(syncAccessRuleSchema),
  /** Holidays (YYYY-MM-DD) that block rules with `blockOnHolidays`. */
  holidays: z.array(z.string()),
  /** Visitor invites the gateway validates offline (QR = `CV1.<id>.<ed25519 signature>`). */
  visitorInvites: z.array(syncVisitorInviteSchema),
  /** ed25519 public key (base64, raw 32 bytes) that signed the QR codes; null when off. */
  visitorInviteKey: z.string().nullable(),
  /** Direct video (ADR-108): STUN servers the gateway's go2rtc uses to learn its public candidates. Absent on older clouds. */
  video: z.object({ stunUrls: z.array(z.string()) }).optional(),
  /** Every live id, so the gateway can delete what disappeared. */
  ids: z.object({
    persons: z.array(uuid),
    credentials: z.array(uuid),
    devices: z.array(uuid),
    cameras: z.array(uuid),
    accessPoints: z.array(uuid),
    accessRules: z.array(uuid),
    visitorInvites: z.array(uuid)
  })
});
var releaseManifestSchema = z.object({
  version: z.string(),
  platform: z.enum(AGENT_PLATFORMS),
  url: z.string().url(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  signature: z.string(),
  size: z.number().int().min(0),
  publishedAt: isoDate
});
var agentConfigSchema = z.object({
  gatewayId: uuid,
  tenantId: uuid,
  cloudUrl: z.string().url(),
  token: z.string(),
  mqttUrl: z.string(),
  mqttUsername: z.string(),
  dataDir: z.string(),
  go2rtcBinary: z.string().nullable(),
  mock: z.boolean(),
  wireguard: z.object({
    interface: z.string(),
    address: z.string(),
    privateKey: z.string(),
    hubPublicKey: z.string(),
    hubEndpoint: z.string(),
    allowedIps: z.string(),
    persistentKeepalive: z.number().int()
  }).nullable()
});
var protocolSchemas = {
  Envelope: envelopeSchema,
  GatewayStatus: gatewayStatusSchema,
  DeviceHealth: deviceHealthSchema,
  Ack: ackSchema,
  OpenDoorCommand: openDoorCommand,
  SyncNowCommand: syncNowCommand,
  EnrollFaceCommand: enrollFaceCommand,
  RemoveFaceCommand: removeFaceCommand,
  RevokeCredentialCommand: revokeCredentialCommand,
  DeviceTestCommand: deviceTestCommand,
  DiscoverChannelsCommand: discoverChannelsCommand,
  GetSnapshotCommand: getSnapshotCommand,
  RestartDriverCommand: restartDriverCommand,
  UpdateAgentCommand: updateAgentCommand,
  GetLogsCommand: getLogsCommand,
  SearchRecordingsCommand: searchRecordingsCommand,
  PrepareStreamCommand: prepareStreamCommand,
  NetProbeCommand: netProbeCommand,
  NetProbeResult: netProbeResult,
  WebrtcOfferCommand: webrtcOfferCommand,
  WebrtcOfferResult: webrtcOfferResult,
  CommandError: commandErrorSchema,
  CommandResultEvent: commandResultEvent,
  HeartbeatEvent: heartbeatEvent,
  DeviceStateEvent: deviceStateEvent,
  SyncDoneEvent: syncDoneEvent,
  AccessEvent: accessEvent,
  DoorEvent: doorEvent,
  AgentUpdatedEvent: agentUpdatedEvent,
  CameraStateEvent: cameraStateEvent,
  SyncPerson: syncPersonSchema,
  SyncCredential: syncCredentialSchema,
  SyncDevice: syncDeviceSchema,
  SyncCamera: syncCameraSchema,
  SyncAccessPoint: syncAccessPointSchema,
  SyncAccessRule: syncAccessRuleSchema,
  SyncResponse: syncResponseSchema,
  ReleaseManifest: releaseManifestSchema,
  AgentConfig: agentConfigSchema
};
function parseCommandEnvelope(input) {
  const envelope = envelopeSchema.parse(input);
  if (!COMMAND_TYPES.includes(envelope.type)) {
    throw new Error(`Unknown command type: ${envelope.type}`);
  }
  const type = envelope.type;
  return { envelope, type, payload: commandSchemas[type].parse(envelope.payload) };
}
function parseEventEnvelope(input) {
  const envelope = envelopeSchema.parse(input);
  if (!EVENT_TYPES.includes(envelope.type)) {
    throw new Error(`Unknown event type: ${envelope.type}`);
  }
  const type = envelope.type;
  return { envelope, type, payload: eventSchemas[type].parse(envelope.payload) };
}

// src/fleet.ts
var SINGLE_LINE = /^[^\p{Cc}\u2028\u2029]*$/u;
var gatewayCreateSchema = z.object({
  tenantId: z.uuid({ error: "Selecione o condom\xEDnio" }),
  name: z.string({ error: "Informe o nome" }).trim().min(1, "Informe o nome").max(80).regex(SINGLE_LINE, "O nome n\xE3o pode ter quebras de linha"),
  notes: z.string().trim().max(500).optional(),
  /** Development helper: marks the gateway as simulated. */
  isMock: z.boolean().optional()
});
var gatewayUpdateSchema = z.object({
  name: z.string().trim().min(1, "Informe o nome").max(80).regex(SINGLE_LINE, "O nome n\xE3o pode ter quebras de linha").optional(),
  notes: z.string().trim().max(500).nullable().optional()
});
var GATEWAY_STATUS_LABELS = {
  pending: "Aguardando instala\xE7\xE3o",
  online: "Online",
  offline: "Offline"
};
var DEVICE_STATUS_LABELS = {
  unknown: "Desconhecido",
  online: "Online",
  offline: "Offline",
  error: "Com erro"
};
function isLoopbackUrl(url) {
  let host;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }
  const bare = host.replace(/^\[|\]$/g, "");
  return bare === "localhost" || bare.endsWith(".localhost") || bare === "0.0.0.0" || bare === "::1" || /^127(\.\d{1,3}){3}$/.test(bare);
}
var DEVICE_TYPES = ["idface", "nvr", "camera", "relay"];
var DEVICE_DRIVERS = {
  idface: ["controlid-idface"],
  nvr: ["hikvision-isapi"],
  camera: ["hikvision-isapi", "rtsp-generic"],
  relay: ["http-relay"]
};
var deviceCredentialsSchema = z.object({
  username: z.string().trim().max(80).optional(),
  password: z.string().max(200).optional(),
  token: z.string().max(500).optional()
});
var deviceCreateSchema = z.object({
  tenantId: z.uuid({ error: "Selecione o condom\xEDnio" }),
  gatewayId: z.uuid({ error: "Selecione o gateway" }).nullable().optional(),
  name: z.string({ error: "Informe o nome" }).trim().min(1, "Informe o nome").max(80),
  type: z.enum(DEVICE_TYPES, { error: "Selecione o tipo" }),
  manufacturer: z.string().trim().max(60).nullable().optional(),
  model: z.string().trim().max(60).nullable().optional(),
  host: z.string({ error: "Informe o IP ou hostname" }).trim().min(1, "Informe o IP ou hostname").max(255),
  port: z.number().int().min(1).max(65535).nullable().optional(),
  driver: z.string().trim().max(40).nullable().optional(),
  credentials: deviceCredentialsSchema.nullable().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().optional()
});
var deviceUpdateSchema = updateSchema(deviceCreateSchema.omit({ tenantId: true }));
var commandRequestSchema = z.object({
  type: z.enum(COMMAND_TYPES, { error: "Comando inv\xE1lido" }),
  payload: z.record(z.string(), z.unknown()).optional(),
  /** Wait for the cmd.result (device test, discovery, logs) up to N seconds. */
  waitSeconds: z.number().int().min(0).max(30).optional()
});
var releaseRegisterSchema = z.object({
  version: z.string({ error: "Informe a vers\xE3o" }).trim().regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, "Use versionamento sem\xE2ntico (ex.: 1.2.0)"),
  platform: z.enum(AGENT_PLATFORMS, { error: "Selecione a plataforma" }),
  objectKey: z.string({ error: "Envie o bin\xE1rio primeiro" }).trim().min(1, "Envie o bin\xE1rio primeiro"),
  notes: z.string().trim().max(500).optional()
});
var releaseUploadSchema = z.object({
  version: z.string().trim().min(1),
  platform: z.enum(AGENT_PLATFORMS)
});
var ALERT_TYPES = [
  "gateway_offline",
  "device_offline",
  "sync_failed",
  "agent_update_failed",
  "door_held_open",
  "camera_offline"
];
var ALERT_TYPE_LABELS = {
  gateway_offline: "Gateway offline",
  device_offline: "Dispositivo offline",
  sync_failed: "Falha de sincroniza\xE7\xE3o",
  agent_update_failed: "Falha ao atualizar o agente",
  door_held_open: "Porta mantida aberta",
  camera_offline: "C\xE2mera offline"
};

// src/roles.ts
var ROLES = [
  "superadmin",
  "org_admin",
  "sindico",
  "subsindico",
  "conselho",
  "zelador",
  "porteiro",
  "morador"
];
var MEMBERSHIP_ROLES = [
  "org_admin",
  "sindico",
  "subsindico",
  "conselho",
  "zelador",
  "porteiro",
  "morador"
];
var RESIDENT_KINDS = ["proprietario", "inquilino", "dependente"];
var ROLE_LABELS = {
  superadmin: "Operador",
  org_admin: "Administradora",
  sindico: "S\xEDndico",
  subsindico: "Subs\xEDndico",
  conselho: "Conselho",
  zelador: "Zelador",
  porteiro: "Porteiro",
  morador: "Morador"
};
var RESIDENT_KIND_LABELS = {
  proprietario: "Propriet\xE1rio",
  inquilino: "Inquilino",
  dependente: "Dependente"
};
var MANAGEMENT_ROLES = ["org_admin", "sindico", "subsindico"];
var STAFF_ROLES = ["zelador", "porteiro"];
var REGISTRY_READ_ROLES = [
  "org_admin",
  "sindico",
  "subsindico",
  "conselho",
  "zelador",
  "porteiro"
];
function isRole(value) {
  return typeof value === "string" && ROLES.includes(value);
}

// src/identity.ts
var forgotPasswordSchema = z.object({
  identifier: z.string({ error: "Informe seu e-mail ou celular" }).trim().min(3, "Informe seu e-mail ou celular").max(160)
});
var resetPasswordSchema = z.object({
  token: z.string().min(20).max(500).optional(),
  code: z.string().regex(/^\d{6}$/, "C\xF3digo de 6 d\xEDgitos").optional(),
  identifier: z.string().trim().min(3).max(160).optional(),
  password: passwordSchema
}).refine((v) => !!v.token || !!v.code && !!v.identifier, {
  message: "Informe o link recebido ou o c\xF3digo e seu e-mail/celular",
  path: ["code"]
});
var changePasswordSchema = z.object({
  currentPassword: z.string({ error: "Informe sua senha atual" }).min(1, "Informe sua senha atual"),
  newPassword: passwordSchema
}).refine((v) => v.currentPassword !== v.newPassword, {
  message: "A nova senha precisa ser diferente da atual",
  path: ["newPassword"]
});
var updateProfileSchema = z.object({
  name: z.string().trim().min(3, "Informe seu nome completo").max(160).optional(),
  phone: z.preprocess((v) => v === "" ? null : v, z.string().trim().nullable()).optional()
});
var totpCode = z.string({ error: "Informe o c\xF3digo" }).trim().regex(/^\d{6}$/, "C\xF3digo de 6 d\xEDgitos");
var mfaConfirmSchema = z.object({ code: totpCode });
var mfaVerifySchema = z.object({
  mfaToken: z.string().min(20, "Sess\xE3o de verifica\xE7\xE3o inv\xE1lida"),
  code: totpCode.optional(),
  recoveryCode: z.string().trim().min(8).max(24).optional()
}).refine((v) => !!v.code || !!v.recoveryCode, {
  message: "Informe o c\xF3digo do aplicativo ou um c\xF3digo de recupera\xE7\xE3o",
  path: ["code"]
});
var mfaDisableSchema = z.object({
  password: z.string({ error: "Informe sua senha" }).min(1, "Informe sua senha"),
  code: totpCode
});
var INVITE_CHANNELS = ["email", "sms", "whatsapp"];
var INVITE_CHANNEL_LABELS = {
  email: "E-mail",
  sms: "SMS",
  whatsapp: "WhatsApp"
};
var inviteCreateSchema = z.object({
  personId: z.uuid({ error: "Selecione a pessoa" }),
  unitId: z.preprocess((v) => v === "" ? null : v, z.uuid().nullable()).optional(),
  role: z.enum(MEMBERSHIP_ROLES, { error: "Selecione o papel" }),
  residentKind: z.preprocess((v) => v === "" ? null : v, z.enum(RESIDENT_KINDS).nullable()).optional(),
  channel: z.enum(INVITE_CHANNELS, { error: "Escolha como enviar" }),
  /** Defaults to the person's e-mail or phone. */
  destination: z.string().trim().max(160).optional(),
  message: z.string().trim().max(300).optional()
}).superRefine((v, ctx) => {
  if (v.role === "morador" && !v.unitId) {
    ctx.addIssue({
      code: "custom",
      path: ["unitId"],
      message: "Moradores precisam de uma unidade"
    });
  }
});
var inviteAcceptSchema = z.object({
  token: z.string().min(20).max(500).optional(),
  code: z.string().trim().regex(/^\d{6}$/, "C\xF3digo de 6 d\xEDgitos").optional(),
  destination: z.string().trim().min(3).max(160).optional(),
  name: z.string().trim().min(3, "Informe seu nome completo").max(160).optional(),
  password: passwordSchema
}).refine((v) => !!v.token || !!v.code && !!v.destination, {
  message: "Use o link recebido ou informe o c\xF3digo e o e-mail/celular",
  path: ["code"]
});
var inviteLookupSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, "C\xF3digo de 6 d\xEDgitos"),
  destination: z.string().trim().min(3).max(160)
});
var STATION_KINDS = ["web_totem", "sip"];
var STATION_KIND_LABELS = {
  web_totem: "Totem web (tablet/PC no port\xE3o)",
  sip: "Interfone SIP (futuro)"
};
var uuid2 = z.uuid({ error: "Identificador inv\xE1lido" });
var stationSchema = z.object({
  name: z.string({ error: "Informe o nome" }).trim().min(2, "Informe o nome").max(80),
  kind: z.enum(STATION_KINDS).default("web_totem"),
  /** Door the station opens during a call (its camera is shown to whoever answers). */
  accessPointId: uuid2.nullable().optional(),
  /** Camera shown to the resident/front desk; defaults to the access point camera. */
  cameraId: uuid2.nullable().optional(),
  location: z.string().trim().max(120).nullable().optional(),
  isActive: z.boolean().default(true)
});
var stationUpdateSchema = updateSchema(stationSchema);
var CALL_TARGETS = ["unit", "frontdesk"];
var CALL_STATUSES = [
  "ringing",
  "ringing_frontdesk",
  "answered",
  "ended",
  "missed",
  "declined",
  "cancelled",
  "failed"
];
var CALL_STATUS_LABELS = {
  ringing: "Chamando",
  ringing_frontdesk: "Chamando a portaria",
  answered: "Em andamento",
  ended: "Encerrada",
  missed: "N\xE3o atendida",
  declined: "Recusada",
  cancelled: "Cancelada no port\xE3o",
  failed: "Falhou"
};
var CALL_ANSWERED_BY = ["resident", "frontdesk"];
var totemCallSchema = z.object({
  target: z.enum(CALL_TARGETS),
  unitId: uuid2.optional()
}).refine((v) => v.target === "frontdesk" || !!v.unitId, {
  message: "Escolha a unidade",
  path: ["unitId"]
});
var callsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  status: z.enum(CALL_STATUSES).optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional()
});
var callEndSchema = z.object({
  reason: z.string().trim().max(120).optional()
});
var RESIDENT_CALL_EVENTS = {
  incoming: "call.incoming",
  answered: "call.answered",
  ended: "call.ended"
};
var INCOMING_CALL_PUSH_TYPE = "incoming_call";
var visitorEntrySchema = z.object({
  name: z.string({ error: "Informe o nome do visitante" }).trim().min(3, "Informe o nome").max(120),
  document: z.string().trim().max(40).nullable().optional(),
  unitId: uuid2.nullable().optional(),
  /** Who authorised the entry (resident name or "portaria"). */
  authorizedBy: z.string().trim().max(120).nullable().optional(),
  /** Key returned by POST /uploads/presign (kind `visitor_photo`). */
  photoKey: z.string().trim().max(300).nullable().optional(),
  notes: z.string().trim().max(300).nullable().optional()
});
var SHIFT_CHECKLIST = [
  { key: "keys", label: "Chaves e controles conferidos" },
  { key: "cameras", label: "C\xE2meras e port\xF5es funcionando" },
  { key: "visitors", label: "Visitantes autorizados do dia repassados" },
  { key: "incidents", label: "Ocorr\xEAncias e recados repassados" },
  { key: "deliveries", label: "Encomendas pendentes conferidas" }
];
var shiftEndSchema = z.object({
  checklist: z.array(z.object({ key: z.string().min(1).max(40), done: z.boolean() })).max(20).default([]),
  notes: z.string().trim().max(1e3).nullable().optional(),
  handedOverToUserId: uuid2.nullable().optional()
});
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25)
});

// src/permissions.ts
var PERMISSIONS = [
  "condominium:read",
  "condominium:settings",
  "condominium:create",
  "registry:read",
  "registry:write",
  "registry:import",
  /** Erase a person's data (LGPD): management only, never the front desk. */
  "registry:erase",
  "credentials:read",
  "credentials:write",
  "devices:read",
  "devices:write",
  "invites:manage",
  "users:read",
  "audit:read",
  "alerts:read",
  "alerts:write",
  "access:open",
  "access:rules",
  "events:read",
  "events:export",
  "cameras:view",
  "cameras:playback",
  "own_unit:read",
  "own_unit:open",
  "calls:read",
  "calls:answer",
  "frontdesk:use",
  "visitors:write",
  "visitors:read",
  "visitors:invite",
  "packages:read",
  "packages:write",
  "announcements:read",
  "announcements:write",
  "finance:read",
  "finance:write"
];
var READ_ALL = [
  "condominium:read",
  "registry:read",
  "credentials:read",
  "devices:read",
  "users:read",
  "alerts:read",
  "events:read",
  "cameras:view",
  "calls:read",
  "visitors:read",
  "packages:read",
  "announcements:read"
];
var RESIDENT_SELF = ["own_unit:read", "own_unit:open", "visitors:invite"];
var FRONT_DESK = [
  "calls:answer",
  "frontdesk:use",
  "visitors:write",
  "packages:write"
];
var FINANCE = ["finance:read", "finance:write"];
var ROLE_PERMISSIONS = {
  org_admin: [
    ...READ_ALL,
    ...RESIDENT_SELF,
    ...FRONT_DESK,
    ...FINANCE,
    "announcements:write",
    "condominium:settings",
    "condominium:create",
    "registry:write",
    "registry:import",
    "registry:erase",
    "credentials:write",
    "devices:write",
    "invites:manage",
    "audit:read",
    "alerts:write",
    "access:open",
    "access:rules",
    "events:export",
    "cameras:playback"
  ],
  sindico: [
    ...READ_ALL,
    ...RESIDENT_SELF,
    ...FRONT_DESK,
    ...FINANCE,
    "announcements:write",
    "condominium:settings",
    "registry:write",
    "registry:import",
    "registry:erase",
    "credentials:write",
    "devices:write",
    "invites:manage",
    "audit:read",
    "alerts:write",
    "access:open",
    "access:rules",
    "events:export",
    "cameras:playback"
  ],
  subsindico: [
    ...READ_ALL,
    ...RESIDENT_SELF,
    ...FRONT_DESK,
    ...FINANCE,
    "announcements:write",
    "registry:write",
    "registry:import",
    "credentials:write",
    "invites:manage",
    "audit:read",
    "alerts:write",
    "access:open",
    "access:rules",
    "events:export",
    "cameras:playback"
  ],
  conselho: [
    ...READ_ALL,
    ...RESIDENT_SELF,
    "finance:read",
    "audit:read",
    "events:export",
    "cameras:playback"
  ],
  zelador: [...READ_ALL, ...RESIDENT_SELF, ...FRONT_DESK, "access:open"],
  porteiro: [...READ_ALL, ...RESIDENT_SELF, ...FRONT_DESK, "access:open"],
  morador: ["condominium:read", ...RESIDENT_SELF]
};
var INVITABLE_ROLES = {
  superadmin: ["org_admin", "sindico", "subsindico", "conselho", "zelador", "porteiro", "morador"],
  org_admin: ["org_admin", "sindico", "subsindico", "conselho", "zelador", "porteiro", "morador"],
  sindico: ["subsindico", "conselho", "zelador", "porteiro", "morador"],
  subsindico: ["zelador", "porteiro", "morador"],
  conselho: [],
  zelador: [],
  porteiro: [],
  morador: []
};
function permissionsOf(role, isSuperadmin) {
  if (isSuperadmin) return [...PERMISSIONS];
  if (!role || role === "superadmin") return [];
  return [...ROLE_PERMISSIONS[role]];
}
function hasPermission(role, isSuperadmin, permission) {
  return permissionsOf(role, isSuperadmin).includes(permission);
}
function roleRequiresMfa(isSuperadmin, roles) {
  return isSuperadmin || roles.includes("org_admin");
}
var OPEN_CONFIRMATIONS = ["tap", "hold", "slide", "device_biometric"];
var residentOpenSchema = z.object({
  confirmation: z.enum(OPEN_CONFIRMATIONS).default("tap")
});
var APP_OPEN_REASON = "Abertura pelo aplicativo";
var residentEventsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  kind: z.enum(ACCESS_EVENT_KINDS).optional(),
  result: z.enum(ACCESS_RESULTS).optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional()
});
var CAPTURE_LIVENESS_CHECKS = ["blink", "turn"];
var residentFaceEnrollSchema = z.object({
  /** Key returned by POST /uploads/presign (kind `face`). */
  objectKey: z.string().trim().min(1, "Envie a foto primeiro"),
  consent: z.object({
    accepted: z.literal(true, { error: "\xC9 preciso aceitar o termo de consentimento" }),
    termVersion: z.literal(BIOMETRIC_CONSENT.version)
  }),
  /** How the photo was taken (audited; the backend still validates the image). */
  capture: z.object({
    source: z.enum(["camera", "gallery"]).default("camera"),
    liveness: z.array(z.enum(CAPTURE_LIVENESS_CHECKS)).default([])
  }).default({ source: "camera", liveness: [] })
});
var PUSH_PLATFORMS = ["android", "ios"];
var PUSH_TOKEN_KINDS = ["fcm", "apns_voip"];
var pushTokenSchema = z.object({
  token: z.string().trim().min(20, "Token inv\xE1lido").max(4096),
  platform: z.enum(PUSH_PLATFORMS),
  kind: z.enum(PUSH_TOKEN_KINDS).default("fcm"),
  deviceName: z.string().trim().max(120).optional(),
  appVersion: z.string().trim().max(40).optional()
});
var NOTIFICATION_TYPES = [
  "access_denied",
  "door_opened",
  "face_synced",
  "face_failed",
  "missed_call",
  "alert",
  "visitor_arrived",
  "package",
  "announcement"
];
var NOTIFICATION_TYPE_LABELS = {
  access_denied: {
    title: "Acesso negado",
    description: "Quando uma credencial sua for recusada em um terminal."
  },
  door_opened: {
    title: "Porta aberta pela unidade",
    description: "Quando algu\xE9m da sua unidade abrir um port\xE3o pelo aplicativo ou pela portaria."
  },
  face_synced: {
    title: "Biometria pronta",
    description: "Quando sua biometria facial for sincronizada nos terminais."
  },
  face_failed: {
    title: "Problema na biometria",
    description: "Quando um terminal recusar sua biometria facial."
  },
  missed_call: {
    title: "Chamada perdida",
    description: "Quando o port\xE3o chamar sua unidade e ningu\xE9m atender."
  },
  alert: {
    title: "Alertas operacionais",
    description: "Gateway ou c\xE2mera offline, porta mantida aberta e falhas de sincroniza\xE7\xE3o."
  },
  visitor_arrived: {
    title: "Visitante chegou",
    description: "Quando um visitante convidado entrar com o QR code."
  },
  package: {
    title: "Encomendas",
    description: "Quando chegar uma encomenda, lembretes enquanto ela esperar e o aviso de retirada."
  },
  announcement: {
    title: "Avisos do condom\xEDnio",
    description: "Quando a administra\xE7\xE3o publicar um aviso para a sua unidade."
  }
};
var hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:mm");
var notificationPreferencesSchema = z.object({
  types: z.partialRecord(z.enum(NOTIFICATION_TYPES), z.boolean()).default({}),
  /** Push is muted inside the window (in-app notifications are still recorded). */
  quietHours: z.object({ from: hhmm, to: hhmm }).nullable().default(null),
  /**
   * Whether the intercom rings this person's phone for calls to their units. Independent of
   * quiet hours (a doorbell is not a notification); omitted = unchanged.
   */
  intercomCalls: z.boolean().optional()
});
var LIVE_QUALITIES = ["sd", "hd"];
var liveQuerySchema = z.object({
  quality: z.enum(LIVE_QUALITIES, { error: "Qualidade inv\xE1lida" }).default("sd")
});
var recordingsQuerySchema = z.object({
  from: z.iso.datetime({ offset: true, error: "Informe o in\xEDcio" }),
  to: z.iso.datetime({ offset: true, error: "Informe o fim" })
}).refine((v) => new Date(v.from) < new Date(v.to), {
  message: "O fim deve ser depois do in\xEDcio",
  path: ["to"]
});
var playbackSchema = z.object({
  from: z.iso.datetime({ offset: true }),
  to: z.iso.datetime({ offset: true })
}).refine((v) => new Date(v.from) < new Date(v.to), {
  message: "O fim deve ser depois do in\xEDcio",
  path: ["to"]
}).refine((v) => new Date(v.to).getTime() - new Date(v.from).getTime() <= 6 * 3600 * 1e3, {
  message: "Escolha um intervalo de at\xE9 6 horas",
  path: ["to"]
});
var LIVE_LAYOUTS = [1, 4, 9];
var VISITOR_INVITE_STATUSES = ["active", "used", "revoked", "expired"];
var VISITOR_INVITE_STATUS_LABELS = {
  active: "V\xE1lido",
  used: "Utilizado",
  revoked: "Cancelado",
  expired: "Expirado"
};
var VISITOR_INVITE_MAX_HOURS = 7 * 24;
var uuid3 = z.uuid({ error: "Identificador inv\xE1lido" });
var visitorInviteSchema = z.object({
  visitorName: z.string({ error: "Informe o nome do visitante" }).trim().min(3, "Informe o nome do visitante").max(120, "Nome muito longo"),
  visitorDocument: z.string().trim().max(40, "Documento muito longo").nullable().optional(),
  visitorPhone: z.string().trim().max(30, "Telefone muito longo").nullable().optional(),
  /** Unit that receives the visitor (residents may only invite to their own units). */
  unitId: uuid3,
  validFrom: z.iso.datetime({ offset: true, error: "Informe o in\xEDcio da validade" }),
  validUntil: z.iso.datetime({ offset: true, error: "Informe o fim da validade" }),
  /** Access points the QR opens; empty = any point with a terminal. */
  accessPointIds: z.array(uuid3).max(20).default([]),
  /** One entry only (default); false lets the visitor come and go inside the window. */
  singleUse: z.boolean().default(true),
  note: z.string().trim().max(300, "Observa\xE7\xE3o muito longa").nullable().optional()
});
var visitorInviteCreateSchema = visitorInviteSchema.superRefine((value, ctx) => {
  const from = Date.parse(value.validFrom);
  const until = Date.parse(value.validUntil);
  if (!(until > from)) {
    ctx.addIssue({
      code: "custom",
      path: ["validUntil"],
      message: "O fim precisa ser depois do in\xEDcio"
    });
  } else if (until - from > VISITOR_INVITE_MAX_HOURS * 36e5) {
    ctx.addIssue({
      code: "custom",
      path: ["validUntil"],
      message: "Um convite vale no m\xE1ximo 7 dias"
    });
  }
});
var visitorInvitesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  status: z.enum(VISITOR_INVITE_STATUSES).optional(),
  /** Visitor name or code. */
  q: z.string().trim().max(80).optional(),
  unitId: uuid3.optional()
});
var visitorCheckinSchema = z.object({
  photoKey: z.string().trim().max(300).nullable().optional(),
  notes: z.string().trim().max(300).nullable().optional()
});
var VISITOR_QR_PREFIX = "CV1";
function visitorQrCanonical(inviteId, tenantId, validFrom, validUntil) {
  const epoch = (value) => Math.floor(new Date(value).getTime() / 1e3);
  return `${inviteId}|${tenantId}|${epoch(validFrom)}|${epoch(validUntil)}`;
}
function parseVisitorQr(payload) {
  const parts = payload.trim().split(".");
  if (parts.length !== 3 || parts[0] !== VISITOR_QR_PREFIX) return null;
  const inviteId = parts[1] ?? "";
  const signature = parts[2] ?? "";
  if (!/^[0-9a-f-]{36}$/i.test(inviteId) || signature.length < 40) return null;
  return { inviteId, signature };
}
function visitorInviteStatus(invite, now = /* @__PURE__ */ new Date()) {
  if (invite.status === "revoked" || invite.status === "used") return invite.status;
  return new Date(invite.validUntil).getTime() < now.getTime() ? "expired" : "active";
}
var PACKAGE_STATUSES = ["received", "picked_up", "returned"];
var PACKAGE_STATUS_LABELS = {
  received: "Aguardando retirada",
  picked_up: "Entregue",
  returned: "Devolvida"
};
var PACKAGE_KINDS = ["box", "envelope", "bag", "perishable", "other"];
var PACKAGE_KIND_LABELS = {
  box: "Caixa",
  envelope: "Envelope",
  bag: "Sacola",
  perishable: "Perec\xEDvel",
  other: "Outro"
};
var PACKAGE_PICKUP_METHODS = ["code", "person"];
var PACKAGE_PICKUP_METHOD_LABELS = {
  code: "C\xF3digo do morador",
  person: "Morador identificado"
};
var PACKAGE_CODE_MAX_ATTEMPTS = 5;
var PACKAGE_STALE_DAYS = 3;
var uuid4 = z.uuid({ error: "Identificador inv\xE1lido" });
var optionalText = (max, message) => z.string().trim().max(max, message).nullable().optional();
var packageSchema = z.object({
  unitId: uuid4,
  /** Person of the unit the package is addressed to; empty = anyone of the unit. */
  recipientPersonId: uuid4.nullable().optional(),
  /** Name on the label when it matches nobody registered. */
  recipientName: optionalText(120, "Nome muito longo"),
  kind: z.enum(PACKAGE_KINDS, { error: "Escolha o tipo da encomenda" }).default("box"),
  carrier: optionalText(80, "Transportadora muito longa"),
  trackingCode: optionalText(80, "Rastreamento muito longo"),
  description: optionalText(200, "Descri\xE7\xE3o muito longa"),
  storageLocation: optionalText(80, "Local muito longo"),
  notes: optionalText(300, "Observa\xE7\xE3o muito longa"),
  /** Key returned by POST /uploads/presign (kind `package_photo`). */
  photoKey: z.string().trim().max(300).nullable().optional()
});
var packageUpdateSchema = updateSchema(packageSchema);
var packagesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  status: z.enum(PACKAGE_STATUSES).optional(),
  unitId: uuid4.optional(),
  /** Recipient, carrier, tracking code or unit number. */
  q: z.string().trim().max(80).optional()
});
var pickupBase = z.object({
  /** Pending packages of one unit handed over together. */
  packageIds: z.array(uuid4).min(1, "Escolha ao menos uma encomenda").max(30),
  method: z.enum(PACKAGE_PICKUP_METHODS),
  /** Method `code`: the 6 digits the resident sees in the app. */
  code: z.string().trim().regex(/^\d{6}$/, "O c\xF3digo tem 6 d\xEDgitos").optional(),
  /** Method `person`: a person with an active link to the unit. */
  personId: uuid4.optional(),
  /** Who took it when it was not the resident (someone sent with the code). */
  pickedUpByName: optionalText(120, "Nome muito longo"),
  pickedUpByDocument: optionalText(40, "Documento muito longo")
});
var packagePickupSchema = pickupBase.superRefine((value, ctx) => {
  if (value.method === "code" && !value.code) {
    ctx.addIssue({ code: "custom", path: ["code"], message: "Informe o c\xF3digo do morador" });
  }
  if (value.method === "person" && !value.personId) {
    ctx.addIssue({ code: "custom", path: ["personId"], message: "Escolha quem est\xE1 retirando" });
  }
});
var packageReturnSchema = z.object({
  reason: z.string({ error: "Informe o motivo da devolu\xE7\xE3o" }).trim().min(3, "Informe o motivo da devolu\xE7\xE3o").max(300, "Motivo muito longo")
});
var packageAuthorizationSchema = z.object({
  name: z.string({ error: "Informe o nome de quem vai retirar" }).trim().min(3, "Informe o nome de quem vai retirar").max(120, "Nome muito longo"),
  document: optionalText(40, "Documento muito longo")
});
function packageWaitingDays(receivedAt, now = /* @__PURE__ */ new Date()) {
  const ms = now.getTime() - new Date(receivedAt).getTime();
  return Math.max(0, Math.floor(ms / 864e5));
}
var CHARGE_STATUSES = ["open", "paid", "canceled"];
var CHARGE_VIEW_STATUSES = ["open", "overdue", "paid", "canceled"];
var CHARGE_STATUS_LABELS = {
  open: "Em aberto",
  overdue: "Em atraso",
  paid: "Paga",
  canceled: "Cancelada"
};
var PAYMENT_METHODS = ["pix", "boleto", "transfer", "cash", "other"];
var PAYMENT_METHOD_LABELS = {
  pix: "PIX",
  boleto: "Boleto",
  transfer: "Transfer\xEAncia",
  cash: "Dinheiro",
  other: "Outro"
};
var FINANCE_TIME_ZONE = "America/Sao_Paulo";
var uuid5 = z.uuid({ error: "Identificador inv\xE1lido" });
var optionalText2 = (max, message) => z.string().trim().max(max, message).nullable().optional();
var competenceSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Compet\xEAncia no formato AAAA-MM");
var isoDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data no formato AAAA-MM-DD").refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), "Data inv\xE1lida");
var amountCentsSchema = z.number({ error: "Informe o valor" }).int("Valor em centavos").min(1, "Informe um valor maior que zero").max(1e9, "Valor muito alto");
var descriptionSchema = z.string({ error: "Informe a descri\xE7\xE3o" }).trim().min(1, "Informe a descri\xE7\xE3o").max(120, "Descri\xE7\xE3o muito longa");
var chargeSchema = z.object({
  unitId: uuid5,
  description: descriptionSchema,
  competence: competenceSchema,
  amountCents: amountCentsSchema,
  dueDate: isoDaySchema,
  notes: optionalText2(300, "Observa\xE7\xE3o muito longa")
});
var chargeUpdateSchema = updateSchema(chargeSchema.omit({ unitId: true }));
var chargeBatchSchema = z.object({
  competence: competenceSchema,
  amountCents: amountCentsSchema,
  dueDate: isoDaySchema,
  /** Defaults to "Taxa condominial MM/AAAA". */
  description: descriptionSchema.optional()
});
var chargePaySchema = z.object({
  paidOn: isoDaySchema,
  /** What came in (partial, or with interest); defaults to the charge amount in the portal. */
  paidAmountCents: amountCentsSchema,
  paymentMethod: z.enum(PAYMENT_METHODS, { error: "Escolha a forma de pagamento" }),
  paymentNotes: optionalText2(300, "Observa\xE7\xE3o muito longa")
});
var chargeCancelSchema = z.object({
  reason: z.string({ error: "Informe o motivo" }).trim().min(1, "Informe o motivo").max(300, "Motivo muito longo")
});
var chargesQuerySchema = paginationQuerySchema.extend({
  status: z.enum([...CHARGE_VIEW_STATUSES, "all"], { error: "Situa\xE7\xE3o inv\xE1lida" }).default("all"),
  competence: competenceSchema.optional(),
  unitId: uuid5.optional(),
  /** Unit (block + number) or description. */
  q: z.string().trim().max(80).optional()
});
var financeSummaryQuerySchema = z.object({ competence: competenceSchema });
function financeToday(now = /* @__PURE__ */ new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: FINANCE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(now);
}
function daysBetween(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 864e5);
}
function chargeViewStatus(status, dueDate, today = financeToday()) {
  if (status === "open" && dueDate < today) return "overdue";
  return status;
}
function competenceLabel(competence) {
  const [year, month] = competence.split("-");
  return `${month}/${year}`;
}
function monthlyChargeDescription(competence) {
  return `Taxa condominial ${competenceLabel(competence)}`;
}
var brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
function formatCentsBRL(cents) {
  return brl.format(cents / 100);
}
var ANNOUNCEMENT_CATEGORIES = [
  "notice",
  "maintenance",
  "assembly",
  "security",
  "event"
];
var ANNOUNCEMENT_CATEGORY_LABELS = {
  notice: "Aviso",
  maintenance: "Manuten\xE7\xE3o",
  assembly: "Assembleia",
  security: "Seguran\xE7a",
  event: "Evento"
};
var ANNOUNCEMENT_STORED_STATUSES = ["draft", "published", "archived"];
var ANNOUNCEMENT_STATUSES = [
  "draft",
  "scheduled",
  "published",
  "expired",
  "archived"
];
var ANNOUNCEMENT_STATUS_LABELS = {
  draft: "Rascunho",
  scheduled: "Agendado",
  published: "Publicado",
  expired: "Expirado",
  archived: "Arquivado"
};
var ANNOUNCEMENT_AUDIENCES = ["all", "blocks", "units"];
var ANNOUNCEMENT_AUDIENCE_LABELS = {
  all: "Todo o condom\xEDnio",
  blocks: "Blocos",
  units: "Unidades"
};
var ANNOUNCEMENT_ACK_REMINDER_HOURS = 48;
var ANNOUNCEMENT_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp"
];
var uuid6 = z.uuid({ error: "Identificador inv\xE1lido" });
var isoDate2 = z.iso.datetime({ offset: true, error: "Data inv\xE1lida" });
var announcementBase = z.object({
  title: z.string({ error: "Informe o t\xEDtulo" }).trim().min(3, "Informe o t\xEDtulo").max(120, "T\xEDtulo muito longo"),
  /** Plain text; line breaks become paragraphs. */
  body: z.string({ error: "Escreva o aviso" }).trim().min(3, "Escreva o aviso").max(5e3, "Texto muito longo (at\xE9 5.000 caracteres)"),
  category: z.enum(ANNOUNCEMENT_CATEGORIES).default("notice"),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES).default("all"),
  blockIds: z.array(uuid6).max(50).default([]),
  unitIds: z.array(uuid6).max(500).default([]),
  /** Empty = as soon as it is published. */
  publishAt: isoDate2.nullable().optional(),
  expiresAt: isoDate2.nullable().optional(),
  pinned: z.boolean().default(false),
  requiresAck: z.boolean().default(false),
  /** Key returned by POST /uploads/presign (kind `announcement_file`). */
  attachmentKey: z.string().trim().max(300).nullable().optional(),
  attachmentName: z.string().trim().max(160).nullable().optional()
});
function checkAnnouncement(value, ctx) {
  if (value.audience === "blocks" && (value.blockIds?.length ?? 0) === 0) {
    ctx.addIssue({ code: "custom", path: ["blockIds"], message: "Escolha ao menos um bloco" });
  }
  if (value.audience === "units" && (value.unitIds?.length ?? 0) === 0) {
    ctx.addIssue({ code: "custom", path: ["unitIds"], message: "Escolha ao menos uma unidade" });
  }
  if (value.expiresAt) {
    const from = value.publishAt ? Date.parse(value.publishAt) : Date.now();
    if (!(Date.parse(value.expiresAt) > from)) {
      ctx.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "A expira\xE7\xE3o precisa ser depois da publica\xE7\xE3o"
      });
    }
  }
}
var announcementSchema = announcementBase.extend({
  /** false keeps it as a draft; true publishes (or schedules when `publishAt` is ahead). */
  publish: z.boolean().default(false)
}).superRefine(checkAnnouncement);
var announcementUpdateSchema = updateSchema(announcementBase).superRefine(checkAnnouncement);
var announcementsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  status: z.enum(ANNOUNCEMENT_STATUSES).optional(),
  category: z.enum(ANNOUNCEMENT_CATEGORIES).optional(),
  q: z.string().trim().max(80).optional()
});
var announcementReadsQuerySchema = z.object({
  /** Only who still has to read (or acknowledge, when required). */
  pending: z.enum(["true", "false"]).default("false").transform((value) => value === "true")
});
function announcementStatus(row, now = /* @__PURE__ */ new Date()) {
  if (row.status !== "published") return row.status;
  if (row.publishAt && new Date(row.publishAt).getTime() > now.getTime()) return "scheduled";
  if (row.expiresAt && new Date(row.expiresAt).getTime() <= now.getTime()) return "expired";
  return "published";
}
var PERSON_ACTIVITY_SOURCES = ["record", "account", "access"];
var PERSON_ACTIVITY_SOURCE_LABELS = {
  record: "Cadastro",
  account: "A\xE7\xF5es da conta",
  access: "Acessos"
};
var personActivityQuerySchema = z.object({
  kind: z.enum(["all", ...PERSON_ACTIVITY_SOURCES]).default("all"),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  /** Opaque cursor returned as `nextCursor` by the previous page. */
  cursor: z.string().regex(/^\d{1,9}:\d{1,9}$/, "Cursor inv\xE1lido").optional()
});

export { ACCESS_DIRECTIONS, ACCESS_DIRECTION_LABELS, ACCESS_EVENT_KINDS, ACCESS_ORIGINS, ACCESS_ORIGIN_LABELS, ACCESS_POINT_TYPES, ACCESS_POINT_TYPE_LABELS, ACCESS_RESULTS, ACCESS_RULE_KINDS, ACCESS_RULE_KIND_LABELS, AGENT_PLATFORMS, ALERT_TYPES, ALERT_TYPE_LABELS, ANNOUNCEMENT_ACK_REMINDER_HOURS, ANNOUNCEMENT_AUDIENCES, ANNOUNCEMENT_AUDIENCE_LABELS, ANNOUNCEMENT_CATEGORIES, ANNOUNCEMENT_CATEGORY_LABELS, ANNOUNCEMENT_FILE_TYPES, ANNOUNCEMENT_STATUSES, ANNOUNCEMENT_STATUS_LABELS, ANNOUNCEMENT_STORED_STATUSES, API_PREFIX, APP_OPEN_REASON, AUDIT_ENTITY_LABELS, AUDIT_VERB_LABELS, AUTHORIZATION_KINDS, AUTHORIZATION_KIND_LABELS, BIOMETRIC_CONSENT, BRAZIL_TIMEZONES, CALL_ANSWERED_BY, CALL_STATUSES, CALL_STATUS_LABELS, CALL_TARGETS, CAMERA_SOURCES, CAMERA_SOURCE_LABELS, CAPTURE_LIVENESS_CHECKS, CHARGE_STATUSES, CHARGE_STATUS_LABELS, CHARGE_VIEW_STATUSES, CLIENT_TYPE_HEADER, COMMAND_TYPES, CONSENT_SIGNERS, CONSENT_SIGNER_LABELS, CREDENTIAL_STATUSES, CREDENTIAL_STATUS_LABELS, CREDENTIAL_TYPES, CREDENTIAL_TYPE_LABELS, DEFAULT_RESIDENT_FEATURES, DEVICE_DRIVERS, DEVICE_STATUS, DEVICE_STATUS_LABELS, DEVICE_TYPES, DEVICE_TYPE_LABELS, ERASED_PERSON_NAME, EVENT_TYPES, FACE_STATUS, FACE_STATUS_LABELS, FINANCE_TIME_ZONE, GATEWAY_STATUS_LABELS, HEARTBEAT_INTERVAL_SECONDS, INCOMING_CALL_PUSH_TYPE, INDIVIDUAL_RULE_PREFIX, INVITABLE_ROLES, INVITE_CHANNELS, INVITE_CHANNEL_LABELS, LIVE_EVENTS, LIVE_LAYOUTS, LIVE_NAMESPACE, LIVE_QUALITIES, MANAGEMENT_ROLES, MANUAL_CREDENTIAL_TYPES, MEMBERSHIP_ROLES, NET_PROBE_HOST_PATTERN, NOTIFICATION_TYPES, NOTIFICATION_TYPE_LABELS, OFFLINE_AFTER_SECONDS, OPEN_CONFIRMATIONS, PACKAGE_CODE_MAX_ATTEMPTS, PACKAGE_KINDS, PACKAGE_KIND_LABELS, PACKAGE_PICKUP_METHODS, PACKAGE_PICKUP_METHOD_LABELS, PACKAGE_STALE_DAYS, PACKAGE_STATUSES, PACKAGE_STATUS_LABELS, PAYMENT_METHODS, PAYMENT_METHOD_LABELS, PERMISSIONS, PERSON_ACTIVITY_SOURCES, PERSON_ACTIVITY_SOURCE_LABELS, PROTOCOL_VERSION, PUSH_PLATFORMS, PUSH_TOKEN_KINDS, REGISTRY_READ_ROLES, RESIDENT_CALL_EVENTS, RESIDENT_FEATURES, RESIDENT_FEATURE_LABELS, RESIDENT_KINDS, RESIDENT_KIND_LABELS, ROLES, ROLE_LABELS, ROLE_PERMISSIONS, SHIFT_CHECKLIST, STAFF_ROLES, STATION_KINDS, STATION_KIND_LABELS, SYNC_STATES, SYNC_STATE_LABELS, UNIT_PERSON_ROLES, UNIT_PERSON_ROLE_LABELS, UPLOAD_KINDS, VISITOR_INVITE_MAX_HOURS, VISITOR_INVITE_STATUSES, VISITOR_INVITE_STATUS_LABELS, VISITOR_QR_PREFIX, WEEKDAY_LABELS, accessEvent, accessEventsQuerySchema, accessPointSchema, accessRuleMembersSchema, accessRuleSchema, accessRuleUpdateSchema, ackSchema, agentConfigSchema, agentUpdatedEvent, announcementReadsQuerySchema, announcementSchema, announcementStatus, announcementUpdateSchema, announcementsQuerySchema, auditActionLabel, auditQuerySchema, blockSchema, callEndSchema, callsQuerySchema, cameraSchema, cameraStateEvent, cameraUpdateSchema, changePasswordSchema, chargeBatchSchema, chargeCancelSchema, chargePaySchema, chargeSchema, chargeUpdateSchema, chargeViewStatus, chargesQuerySchema, cnpjSchema, commandErrorSchema, commandRequestSchema, commandResultEvent, commandSchemas, competenceLabel, competenceSchema, condominiumCreateSchema, condominiumSchema, cpfSchema, credentialCreateSchema, credentialUpdateSchema, daysBetween, deviceCreateSchema, deviceCredentialsSchema, deviceHealthSchema, deviceStateEvent, deviceTestCommand, deviceUpdateSchema, discoverChannelsCommand, doorEvent, emailInputSchema, endLinkSchema, enrollFaceCommand, envelopeSchema, eventSchemas, faceEnrollSchema, financeSummaryQuerySchema, financeToday, forgotPasswordSchema, formatCentsBRL, formatCnpj, formatCpf, formatPhoneBR, formatPlate, gatewayCreateSchema, gatewayStatusSchema, gatewayUpdateSchema, getLogsCommand, getSnapshotCommand, hasPermission, heartbeatEvent, holidaySchema, inviteAcceptSchema, inviteCreateSchema, inviteLookupSchema, isLoopbackUrl, isRole, isValidCnpj, isValidCpf, isValidPlate, isValidTimezone, isoDaySchema, liveQuerySchema, loginSchema, looksLikeEmail, maskCpf, mfaConfirmSchema, mfaDisableSchema, mfaVerifySchema, monthlyChargeDescription, netProbeCommand, netProbePing, netProbeResult, netProbeTcp, normalizeCnpj, normalizeCpf, normalizePhoneBR, normalizePlate, notificationPreferencesSchema, openDoorCommand, openDoorSchema, organizationSchema, organizationUpdateSchema, packageAuthorizationSchema, packagePickupSchema, packageReturnSchema, packageSchema, packageUpdateSchema, packageWaitingDays, packagesQuerySchema, paginationQuerySchema, parkingSpaceSchema, parseCommandEnvelope, parseEventEnvelope, parseVisitorQr, passwordSchema, permissionsOf, permissiveResidentFeatures, personAccessSchema, personActivityQuerySchema, personSchema, phoneE164Schema, phoneInputSchema, plateFormatOf, plateSchema, playbackSchema, prepareStreamCommand, presignUploadSchema, protocolSchemas, pushTokenSchema, recordingsQuerySchema, refreshSchema, releaseManifestSchema, releaseRegisterSchema, releaseUploadSchema, removeFaceCommand, resetPasswordSchema, residentEventsQuerySchema, residentFaceEnrollSchema, residentFeatureFor, residentFeaturesFor, residentOpenSchema, restartDriverCommand, revokeCredentialCommand, roleRequiresMfa, scheduleSchema, searchRecordingsCommand, shiftEndSchema, stationSchema, stationUpdateSchema, switchCondominiumSchema, syncAccessPointSchema, syncAccessRuleSchema, syncCameraSchema, syncCredentialSchema, syncDeviceSchema, syncDoneEvent, syncNowCommand, syncPersonSchema, syncResponseSchema, syncScheduleSchema, syncVisitorInviteSchema, timezoneSchema, topics, totemCallSchema, unitPersonSchema, unitPersonUpdateSchema, unitSchema, unitsImportOptionsSchema, updateAgentCommand, updateProfileSchema, updateSchema, vehicleSchema, visitorCheckinSchema, visitorEntrySchema, visitorInviteCreateSchema, visitorInviteSchema, visitorInviteStatus, visitorInvitesQuerySchema, visitorQrCanonical, webrtcOfferCommand, webrtcOfferResult };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map